import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Edit, Trash2, Receipt, Sparkles, Tag, Calendar, DollarSign, FileText, Loader2, Link2, Unlink, Plus, Check, Paperclip, RefreshCw } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import { formatCurrency } from '../../lib/utils';
import SelectBookingOrProjectModal from '../manage/SelectBookingOrProjectModal';
import { useAppSystem } from '../../hooks/useAppSystem';
import { approveExpense, expenseNeedsApproval, expenseReceiptFailed } from '../../lib/expenseApproval';
import { reprocessExpenseReceipt } from '../../lib/expenseReceiptBackgroundProcessor';
import ExpenseReceiptPreview from './ExpenseReceiptPreview';
import { getExpenseReceiptInfo } from '../../lib/expenseReceiptDisplay';
import {
  computeFileSha256,
  findDuplicateReceipt,
  formatDuplicateReceiptMessage,
} from '../../lib/expenseReceiptDuplicateCheck';

interface ExpenseCategory {
  id: string;
  name: string;
  color?: string;
}

interface Expense {
  id: string;
  business_id: string;
  booking_id?: string | null;
  project_id?: string | null;
  category_id?: string;
  amount: number;
  currency: string;
  description: string;
  merchant_name?: string;
  expense_date: string;
  payment_method?: string;
  tax_amount?: number;
  tax_rate?: number;
  tax_breakdown?: { name: string; rate: number | null; amount: number }[];
  is_tax_deductible: boolean;
  receipt_image_url?: string;
  ai_categorized: boolean;
  ai_category_suggestion?: string;
  ai_confidence_score?: number;
  ai_extracted_data?: any;
  approval_status?: 'pending' | 'approved';
  approved_at?: string | null;
  notes?: string;
  created_at: string;
  updated_at: string;
  category?: ExpenseCategory;
  receipts?: any[];
  line_items?: any[];
}

interface ExpenseDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  expense: Expense | null;
  onUpdate: () => void;
  onDelete: (expenseId: string) => void;
  categories: ExpenseCategory[];
  onCategoriesChange: () => void;
}

export default function ExpenseDetailsModal({
  isOpen,
  onClose,
  expense,
  onUpdate,
  onDelete,
  categories,
  onCategoriesChange
}: ExpenseDetailsModalProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isReprocessing, setIsReprocessing] = useState(false);
  const [isAttachingInvoice, setIsAttachingInvoice] = useState(false);
  const attachInvoiceInputRef = useRef<HTMLInputElement>(null);
  
  // Linked booking/project state
  const [linkedBooking, setLinkedBooking] = useState<{ id: string; title: string; booking_number: number | null } | null>(null);
  const [linkedProject, setLinkedProject] = useState<{ id: string; title: string } | null>(null);
  const [isLoadingLinks, setIsLoadingLinks] = useState(false);
  const [isSelectBookingOrProjectModalOpen, setIsSelectBookingOrProjectModalOpen] = useState(false);
  
  // App system hooks
  const { coreApps, installedAddons } = useAppSystem();
  const hasCalendarApp = coreApps.some(app => app.app?.slug === 'calendar') || 
                        installedAddons.some(app => app.app?.slug === 'calendar');
  const hasProjectsApp = coreApps.some(app => app.app?.slug === 'projects') || 
                         installedAddons.some(app => app.app?.slug === 'projects');

  // Form state
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [merchantName, setMerchantName] = useState('');
  const [expenseDate, setExpenseDate] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [taxBreakdown, setTaxBreakdown] = useState<{ name: string; rate: string; amount: string }[]>([]);
  const [notes, setNotes] = useState('');
  const [isTaxDeductible, setIsTaxDeductible] = useState(true);

  // Inline add-category
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isSavingCategory, setIsSavingCategory] = useState(false);

  // Animation & responsive
  const [isAnimating, setIsAnimating] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < 640
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  const handleAddCategory = async () => {
    if (!expense) return;
    const trimmed = newCategoryName.trim();
    if (!trimmed) return;
    if (categories.some(c => c.name.toLowerCase() === trimmed.toLowerCase())) {
      toast.error('Category already exists');
      return;
    }
    setIsSavingCategory(true);
    try {
      const maxOrder = categories.reduce((max, c: any) => Math.max(max, c.order_index || 0), -1);
      const { data, error } = await supabase
        .from('expense_categories')
        .insert({ business_id: expense.business_id, name: trimmed, color: '#3B82F6', order_index: maxOrder + 1, is_system: false, is_active: true })
        .select()
        .single();
      if (error) throw error;
      toast.success(`Category "${trimmed}" added`);
      setCategoryId(data.id);
      setNewCategoryName('');
      setIsAddingCategory(false);
      onCategoriesChange();
    } catch (error: any) {
      console.error('Error adding category:', error);
      toast.error(error.message || 'Failed to add category');
    } finally {
      setIsSavingCategory(false);
    }
  };

  // Load linked booking and project details
  useEffect(() => {
    if (expense && isOpen) {
      loadLinkedBookingAndProject();
    } else {
      setLinkedBooking(null);
      setLinkedProject(null);
    }
  }, [expense, isOpen]);

  const loadLinkedBookingAndProject = async () => {
    if (!expense) return;
    
    setIsLoadingLinks(true);
    try {
      if (expense.booking_id) {
        const { data: bookingData, error: bookingError } = await supabase
          .from('bookings')
          .select('id, title, booking_number')
          .eq('id', expense.booking_id)
          .single();
        
        if (!bookingError && bookingData) {
          setLinkedBooking({
            id: bookingData.id,
            title: bookingData.title || 'Untitled Booking',
            booking_number: bookingData.booking_number
          });
        }
      } else {
        setLinkedBooking(null);
      }

      if (expense.project_id) {
        const { data: projectData, error: projectError } = await supabase
          .from('projects')
          .select('id, title')
          .eq('id', expense.project_id)
          .single();
        
        if (!projectError && projectData) {
          setLinkedProject({
            id: projectData.id,
            title: projectData.title || 'Untitled Project'
          });
        }
      } else {
        setLinkedProject(null);
      }
    } catch (error) {
      console.error('Error loading linked booking/project:', error);
    } finally {
      setIsLoadingLinks(false);
    }
  };

  const initTaxBreakdown = (exp: Expense) => {
    if (Array.isArray(exp.tax_breakdown) && exp.tax_breakdown.length > 0) {
      return exp.tax_breakdown.map(t => ({
        name: t.name || 'Tax',
        rate: t.rate != null ? String(t.rate) : '',
        amount: String(t.amount || 0),
      }));
    } else if (exp.tax_amount && exp.tax_amount > 0) {
      return [{ name: 'Tax', rate: '', amount: String(exp.tax_amount) }];
    }
    return [];
  };

  // Initialize form when expense changes
  useEffect(() => {
    if (expense) {
      setAmount(expense.amount.toString());
      setDescription(expense.description);
      setMerchantName(expense.merchant_name || '');
      setExpenseDate(expense.expense_date);
      setCategoryId(expense.category_id || '');
      setPaymentMethod(expense.payment_method || '');
      setTaxBreakdown(initTaxBreakdown(expense));
      setNotes(expense.notes || '');
      setIsTaxDeductible(expense.is_tax_deductible);
      setIsEditing(false);
    }
  }, [expense]);

  const handleSave = async () => {
    if (!expense || !amount || !description || !expenseDate) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSaving(true);
    try {
      const parsedTaxBreakdown = taxBreakdown
        .filter(t => t.name.trim() && parseFloat(t.amount) > 0)
        .map(t => ({
          name: t.name.trim(),
          rate: t.rate ? parseFloat(t.rate) : null,
          amount: parseFloat(t.amount) || 0,
        }));
      const totalTax = parsedTaxBreakdown.reduce((sum, t) => sum + t.amount, 0);

      const { error } = await supabase
        .from('expenses')
        .update({
          amount: parseFloat(amount),
          description,
          merchant_name: merchantName || null,
          expense_date: expenseDate,
          category_id: categoryId || null,
          payment_method: paymentMethod || null,
          tax_amount: totalTax,
          tax_breakdown: parsedTaxBreakdown,
          is_tax_deductible: isTaxDeductible,
          notes: notes || null,
          manually_categorized: true
        })
        .eq('id', expense.id);

      if (error) throw error;

      toast.success('Expense updated successfully');
      setIsEditing(false);
      onUpdate();
    } catch (error: any) {
      console.error('Error updating expense:', error);
      toast.error(error.message || 'Failed to update expense');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!expense) return;
    if (!confirm('Are you sure you want to delete this expense? This action cannot be undone.')) {
      return;
    }

    setIsDeleting(true);
    try {
      onDelete(expense.id);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleUnlinkBooking = async () => {
    if (!expense) return;
    
    try {
      let updateData: any = { booking_id: null };
      
      if (expense.project_id) {
        const { data: projectData } = await supabase
          .from('projects')
          .select('booking_id')
          .eq('id', expense.project_id)
          .single();
        
        if (projectData?.booking_id === expense.booking_id) {
          updateData.project_id = null;
        }
      }
      
      const { error } = await supabase
        .from('expenses')
        .update(updateData)
        .eq('id', expense.id);
      
      if (error) throw error;
      
      toast.success('Expense unlinked from booking');
      await loadLinkedBookingAndProject();
      onUpdate();
    } catch (error: any) {
      console.error('Error unlinking booking:', error);
      toast.error(error.message || 'Failed to unlink booking');
    }
  };

  const handleUnlinkProject = async () => {
    if (!expense) return;
    
    try {
      let updateData: any = { project_id: null };
      
      if (expense.booking_id) {
        const { data: bookingData } = await supabase
          .from('bookings')
          .select('project_id')
          .eq('id', expense.booking_id)
          .single();
        
        if (bookingData?.project_id === expense.project_id) {
          updateData.booking_id = null;
        }
      }
      
      const { error } = await supabase
        .from('expenses')
        .update(updateData)
        .eq('id', expense.id);
      
      if (error) throw error;
      
      toast.success('Expense unlinked from project');
      await loadLinkedBookingAndProject();
      onUpdate();
    } catch (error: any) {
      console.error('Error unlinking project:', error);
      toast.error(error.message || 'Failed to unlink project');
    }
  };

  const handleSelectBooking = async (booking: any) => {
    if (!expense) return;
    
    try {
      let finalBookingId = booking.id;
      let finalProjectId = expense.project_id || null;

      const { data: bookingData } = await supabase
        .from('bookings')
        .select('project_id')
        .eq('id', finalBookingId)
        .single();
      
      if (bookingData?.project_id) {
        finalProjectId = bookingData.project_id;
      } else {
        const { data: projectWithBooking } = await supabase
          .from('projects')
          .select('id')
          .eq('booking_id', finalBookingId)
          .maybeSingle();
        
        if (projectWithBooking?.id) {
          finalProjectId = projectWithBooking.id;
        }
      }

      const { error } = await supabase
        .from('expenses')
        .update({
          booking_id: finalBookingId,
          project_id: finalProjectId
        })
        .eq('id', expense.id);
      
      if (error) throw error;
      
      toast.success('Expense assigned to booking');
      await loadLinkedBookingAndProject();
      onUpdate();
      setIsSelectBookingOrProjectModalOpen(false);
    } catch (error: any) {
      console.error('Error assigning booking:', error);
      toast.error(error.message || 'Failed to assign booking');
    }
  };

  const handleSelectProject = async (project: any) => {
    if (!expense) return;
    
    try {
      let finalProjectId = project.id;
      let finalBookingId = expense.booking_id || null;

      const { data: projectData } = await supabase
        .from('projects')
        .select('booking_id')
        .eq('id', finalProjectId)
        .single();
      
      if (projectData?.booking_id) {
        finalBookingId = projectData.booking_id;
      }

      const { error } = await supabase
        .from('expenses')
        .update({
          booking_id: finalBookingId,
          project_id: finalProjectId
        })
        .eq('id', expense.id);
      
      if (error) throw error;
      
      toast.success('Expense assigned to project');
      await loadLinkedBookingAndProject();
      onUpdate();
      setIsSelectBookingOrProjectModalOpen(false);
    } catch (error: any) {
      console.error('Error assigning project:', error);
      toast.error(error.message || 'Failed to assign project');
    }
  };

  /**
   * Manually attach a supplier invoice / receipt file to an expense that has
   * none (e.g. a PO bill created via "Record Bill").
   */
  const handleAttachInvoice = async (file: File) => {
    if (!expense) return;
    try {
      setIsAttachingInvoice(true);

      const fileHash = await computeFileSha256(file);
      const duplicate = await findDuplicateReceipt(expense.business_id, fileHash);
      if (duplicate && duplicate.expenseId !== expense.id) {
        toast.error(formatDuplicateReceiptMessage(duplicate));
        return;
      }

      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `expense-receipts/${expense.business_id}/${expense.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('user-uploads')
        .upload(filePath, file);
      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('user-uploads')
        .getPublicUrl(filePath);

      const { error: expenseError } = await supabase
        .from('expenses')
        .update({
          receipt_image_path: filePath,
          receipt_image_url: publicUrl,
          updated_at: new Date().toISOString(),
        })
        .eq('id', expense.id);
      if (expenseError) throw expenseError;

      const { error: receiptError } = await supabase.from('expense_receipts').insert({
        expense_id: expense.id,
        business_id: expense.business_id,
        file_name: file.name,
        file_path: filePath,
        file_url: publicUrl,
        file_size: file.size,
        mime_type: file.type,
        file_hash: fileHash,
        ai_processed: false,
        processing_status: 'completed',
      });
      if (receiptError) throw receiptError;

      toast.success('Invoice attached');
      onUpdate();
    } catch (err) {
      console.error('Error attaching invoice:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to attach invoice');
    } finally {
      setIsAttachingInvoice(false);
      if (attachInvoiceInputRef.current) attachInvoiceInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 200);
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  if ((!isOpen && !isAnimating) || !expense) return null;

  const resetForm = () => {
    if (expense) {
      setAmount(expense.amount.toString());
      setDescription(expense.description);
      setMerchantName(expense.merchant_name || '');
      setExpenseDate(expense.expense_date);
      setCategoryId(expense.category_id || '');
      setPaymentMethod(expense.payment_method || '');
      setTaxBreakdown(initTaxBreakdown(expense));
      setNotes(expense.notes || '');
      setIsTaxDeductible(expense.is_tax_deductible);
    }
  };

  const modalContent = (
    <div 
      className={`fixed inset-0 transition-opacity duration-200 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      }`}
      style={{
        zIndex: 100065,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
      onClick={handleClose}
      onTransitionEnd={handleTransitionEnd}
    >
      <div 
        className={`bg-white transform transition-all duration-200 ${
          isMobile 
            ? `fixed left-0 right-0 w-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{ 
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 100050,
          top: 'env(safe-area-inset-top, 0px)',
          bottom: 'env(safe-area-inset-bottom, 0px)',
          ...(isMobile && { left: 0, right: 0 }),
          ...(!isMobile && { height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))' }),
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden relative">
          {/* Header */}
          <div className={`bg-gray-50 border-b border-gray-200 ${isMobile ? 'px-4 py-3' : 'px-6 py-4'} flex items-center justify-between sticky top-0 z-10`}>
            <div className="flex items-center gap-2 min-w-0">
              <h2 className={`${isMobile ? 'text-base' : 'text-lg'} font-semibold text-gray-900 truncate`}>Expense Details</h2>
              {expense.ai_categorized ? (
                <span className="text-[10px] bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded flex items-center gap-1 flex-shrink-0">
                  <Sparkles className="h-3 w-3" />
                  Categorized by AI
                </span>
              ) : null}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {!isEditing && expense && expenseReceiptFailed(expense) ? (
                <button
                  type="button"
                  onClick={async () => {
                    if (!expense || isReprocessing) return;
                    setIsReprocessing(true);
                    try {
                      await reprocessExpenseReceipt({
                        expenseId: expense.id,
                        businessId: expense.business_id,
                        categories,
                      });
                      onUpdate();
                    } catch (err) {
                      console.error('Error reprocessing receipt:', err);
                      toast.error(err instanceof Error ? err.message : 'Failed to reprocess receipt');
                    } finally {
                      setIsReprocessing(false);
                    }
                  }}
                  disabled={isReprocessing}
                  className="flex items-center gap-1 rounded-lg bg-gray-900 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-gray-800 disabled:opacity-60 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  {isReprocessing ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5" />
                  )}
                  Reprocess
                </button>
              ) : null}
              {!isEditing && expenseNeedsApproval(expense) ? (
                <button
                  type="button"
                  onClick={async () => {
                    if (!expense) return;
                    setIsApproving(true);
                    try {
                      await approveExpense(expense.id);
                      toast.success('Marked as reviewed');
                      onUpdate();
                    } catch (err) {
                      console.error('Error approving expense:', err);
                      toast.error('Failed to approve expense');
                    } finally {
                      setIsApproving(false);
                    }
                  }}
                  disabled={isApproving}
                  className="flex items-center gap-1 rounded-lg bg-emerald-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-60 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  {isApproving ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                  )}
                  Approve
                </button>
              ) : null}
              {!isEditing && (
                <>
                  <button
                    onClick={() => setIsEditing(true)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                    title="Edit"
                  >
                    <Edit className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`} />
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={isDeleting}
                    className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors disabled:opacity-50 touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                    title="Delete"
                  >
                    <Trash2 className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`} />
                  </button>
                </>
              )}
              <button
                onClick={handleClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <X className={`${isMobile ? 'h-5 w-5' : 'h-5 w-5'}`} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className={`flex-1 overflow-y-auto overflow-x-hidden min-w-0 ${isMobile ? 'px-4 py-4' : 'px-6 py-6'} space-y-5`}>
            {expense && expenseReceiptFailed(expense) && !isEditing ? (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
                <p className="font-medium">Receipt processing failed</p>
                <p className="mt-1 text-red-800">
                  {expense.notes?.trim() || 'AI didn’t finish reading this receipt. Reprocess to try again.'}
                </p>
              </div>
            ) : null}

            {expenseNeedsApproval(expense) && !isEditing ? (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                Categorized by AI. Worth a quick double-check. This expense already counts in your totals.
              </div>
            ) : null}

            {/* Receipt Image */}
            {expense.receipt_image_url ? (
              <div>
                <label className={`block ${isMobile ? 'text-xs' : 'text-sm'} font-medium text-gray-700 mb-2`}>Receipt</label>
                <ExpenseReceiptPreview
                  {...getExpenseReceiptInfo(expense)}
                  url={expense.receipt_image_url}
                  variant="full"
                />
              </div>
            ) : !isEditing ? (
              <div>
                <label className={`block ${isMobile ? 'text-xs' : 'text-sm'} font-medium text-gray-700 mb-2`}>Receipt</label>
                <input
                  ref={attachInvoiceInputRef}
                  type="file"
                  accept="image/*,application/pdf"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleAttachInvoice(file);
                  }}
                />
                <button
                  type="button"
                  onClick={() => attachInvoiceInputRef.current?.click()}
                  disabled={isAttachingInvoice}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm bg-white border border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-blue-400 hover:text-blue-600 disabled:opacity-50 transition-colors touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  {isAttachingInvoice ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Attaching…
                    </>
                  ) : (
                    <>
                      <Paperclip className="h-4 w-4" />
                      Attach Invoice / Receipt
                    </>
                  )}
                </button>
              </div>
            ) : null}

            {/* Amount (Pre-tax) */}
            <div className="bg-gray-50 rounded-2xl p-4">
              <div className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-500 mb-1`}>Subtotal (Pre-tax)</div>
              {isEditing ? (
                <div className="relative">
                  <DollarSign className={`absolute ${isMobile ? 'left-3' : 'left-4'} top-1/2 transform -translate-y-1/2 ${isMobile ? 'h-5 w-5' : 'h-6 w-6'} text-gray-400`} />
                  <input
                    type="number"
                    step="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className={`w-full ${isMobile ? 'pl-10 pr-3 py-3 text-xl' : 'pl-12 pr-4 py-3 text-2xl'} font-bold bg-white border border-gray-200 rounded-xl text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all`}
                  />
                </div>
              ) : (
                <div className={`${isMobile ? 'text-2xl' : 'text-3xl'} font-bold text-gray-900`}>
                  {formatCurrency(expense.amount)}
                </div>
              )}
            </div>

            {/* Description & Date */}
            <div className={`grid ${isMobile ? 'grid-cols-1 gap-4' : 'grid-cols-2 gap-4'}`}>
              <div>
                <label className={`block ${isMobile ? 'text-xs' : 'text-sm'} font-medium text-gray-700 ${isMobile ? 'mb-1.5' : 'mb-2'}`}>
                  Description {isEditing && <span className="text-red-500">*</span>}
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className={`w-full ${isMobile ? 'px-3 py-3 text-base' : 'px-4 py-3.5 text-base'} bg-gray-50 border border-gray-200 rounded-xl text-gray-900 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all`}
                    required
                  />
                ) : (
                  <div className={`${isMobile ? 'px-3 py-3 text-base' : 'px-4 py-3.5'} bg-gray-50 rounded-xl text-gray-900`}>
                    {expense.description}
                  </div>
                )}
              </div>
              <div>
                <label className={`block ${isMobile ? 'text-xs' : 'text-sm'} font-medium text-gray-700 ${isMobile ? 'mb-1.5' : 'mb-2'}`}>
                  Date {isEditing && <span className="text-red-500">*</span>}
                </label>
                {isEditing ? (
                  <input
                    type="date"
                    value={expenseDate}
                    onChange={(e) => setExpenseDate(e.target.value)}
                    className={`w-full ${isMobile ? 'px-3 py-3 text-base' : 'px-4 py-3.5 text-base'} bg-gray-50 border border-gray-200 rounded-xl text-gray-900 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all`}
                    required
                  />
                ) : (
                  <div className={`${isMobile ? 'px-3 py-3 text-base' : 'px-4 py-3.5'} bg-gray-50 rounded-xl text-gray-900`}>
                    {new Date(expense.expense_date).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>

            {/* Merchant */}
            <div>
              <label className={`block ${isMobile ? 'text-xs' : 'text-sm'} font-medium text-gray-700 ${isMobile ? 'mb-1.5' : 'mb-2'}`}>
                Merchant/Vendor
              </label>
              {isEditing ? (
                <input
                  type="text"
                  value={merchantName}
                  onChange={(e) => setMerchantName(e.target.value)}
                  className={`w-full ${isMobile ? 'px-3 py-3 text-base' : 'px-4 py-3.5 text-base'} bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder:text-gray-400 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all`}
                  placeholder="Store or vendor name"
                />
              ) : (
                <div className={`${isMobile ? 'px-3 py-3 text-base' : 'px-4 py-3.5'} bg-gray-50 rounded-xl text-gray-900`}>
                  {expense.merchant_name || '—'}
                </div>
              )}
            </div>

            {/* Category & Payment Method */}
            <div className={`grid ${isMobile ? 'grid-cols-1 gap-4' : 'grid-cols-2 gap-4'}`}>
              <div>
                <label className={`block ${isMobile ? 'text-xs' : 'text-sm'} font-medium text-gray-700 ${isMobile ? 'mb-1.5' : 'mb-2'}`}>
                  Category
                </label>
                {isEditing ? (
                  isAddingCategory ? (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newCategoryName}
                        onChange={e => setNewCategoryName(e.target.value)}
                        className={`flex-1 ${isMobile ? 'px-3 py-3 text-base' : 'px-4 py-3.5 text-base'} bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder:text-gray-400 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all`}
                        placeholder="Category name"
                        autoFocus
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleAddCategory();
                          if (e.key === 'Escape') { setIsAddingCategory(false); setNewCategoryName(''); }
                        }}
                      />
                      <button
                        type="button"
                        onClick={handleAddCategory}
                        disabled={isSavingCategory || !newCategoryName.trim()}
                        className="px-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center"
                      >
                        {isSavingCategory ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setIsAddingCategory(false); setNewCategoryName(''); }}
                        className="px-3 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="relative">
                      <Tag className={`absolute ${isMobile ? 'left-3' : 'left-4'} top-1/2 transform -translate-y-1/2 ${isMobile ? 'h-4 w-4' : 'h-5 w-5'} text-gray-400 z-10`} />
                      <select
                        value={categoryId}
                        onChange={(e) => {
                          if (e.target.value === '__add_new__') {
                            setIsAddingCategory(true);
                          } else {
                            setCategoryId(e.target.value);
                          }
                        }}
                        className={`w-full ${isMobile ? 'pl-10 pr-3 py-3 text-base' : 'pl-12 pr-4 py-3.5 text-base'} bg-gray-50 border border-gray-200 rounded-xl text-gray-900 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all appearance-none`}
                      >
                        <option value="">Select category</option>
                        {categories.map(category => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                        <option value="__add_new__">+ Add New Category</option>
                      </select>
                    </div>
                  )
                ) : (
                  <div className={`${isMobile ? 'px-3 py-3' : 'px-4 py-3.5'} bg-gray-50 rounded-xl`}>
                    {expense.category ? (
                      <span
                        className="px-2 py-1 rounded text-sm"
                        style={{
                          backgroundColor: expense.category.color ? `${expense.category.color}20` : '#F3F4F6',
                          color: expense.category.color || '#6B7280'
                        }}
                      >
                        {expense.category.name}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </div>
                )}
              </div>
              <div>
                <label className={`block ${isMobile ? 'text-xs' : 'text-sm'} font-medium text-gray-700 ${isMobile ? 'mb-1.5' : 'mb-2'}`}>
                  Payment Method
                </label>
                {isEditing ? (
                  <select
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className={`w-full ${isMobile ? 'px-3 py-3 text-base' : 'px-4 py-3.5 text-base'} bg-gray-50 border border-gray-200 rounded-xl text-gray-900 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all appearance-none`}
                  >
                    <option value="">Select method</option>
                    <option value="cash">Cash</option>
                    <option value="credit_card">Credit Card</option>
                    <option value="debit_card">Debit Card</option>
                    <option value="check">Check</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="other">Other</option>
                  </select>
                ) : (
                  <div className={`${isMobile ? 'px-3 py-3 text-base' : 'px-4 py-3.5'} bg-gray-50 rounded-xl text-gray-900 capitalize`}>
                    {expense.payment_method?.replace('_', ' ') || '—'}
                  </div>
                )}
              </div>
            </div>

            {/* Tax Breakdown */}
            <div>
              <label className={`block ${isMobile ? 'text-xs' : 'text-sm'} font-medium text-gray-700 ${isMobile ? 'mb-1.5' : 'mb-2'}`}>
                Tax
              </label>
              {isEditing ? (
                <div className="space-y-2">
                  {taxBreakdown.map((tax, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <input
                        type="text"
                        value={tax.name}
                        onChange={e => {
                          const updated = [...taxBreakdown];
                          updated[idx] = { ...updated[idx], name: e.target.value };
                          setTaxBreakdown(updated);
                        }}
                        className={`${isMobile ? 'w-24 px-2 py-2.5 text-sm' : 'w-28 px-3 py-3 text-sm'} bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder:text-gray-400 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all`}
                        placeholder="GST"
                      />
                      <div className="relative flex-1">
                        <DollarSign className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="number"
                          step="0.01"
                          value={tax.amount}
                          onChange={e => {
                            const updated = [...taxBreakdown];
                            updated[idx] = { ...updated[idx], amount: e.target.value };
                            setTaxBreakdown(updated);
                          }}
                          className={`w-full pl-8 pr-2 ${isMobile ? 'py-2.5 text-sm' : 'py-3 text-sm'} bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder:text-gray-400 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all`}
                          placeholder="0.00"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => setTaxBreakdown(taxBreakdown.filter((_, i) => i !== idx))}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0 touch-manipulation"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setTaxBreakdown([...taxBreakdown, { name: '', rate: '', amount: '' }])}
                    className={`flex items-center gap-1.5 ${isMobile ? 'text-xs px-3 py-2' : 'text-sm px-3 py-2'} text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors font-medium touch-manipulation`}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add Tax
                  </button>
                  {taxBreakdown.length > 0 && (
                    <div className={`flex justify-between items-center px-3 py-2 bg-gray-50 rounded-xl text-sm`}>
                      <span className="text-gray-500 font-medium">Total Tax</span>
                      <span className="font-semibold text-gray-900">
                        ${taxBreakdown.reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0).toFixed(2)}
                      </span>
                    </div>
                  )}
                  {amount && (
                    <div className="flex justify-between items-center px-3 py-2 bg-blue-50 rounded-xl text-sm">
                      <span className="text-blue-700 font-medium">Grand Total</span>
                      <span className="font-bold text-blue-900">
                        ${((parseFloat(amount) || 0) + taxBreakdown.reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0)).toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-1.5">
                  {(expense.tax_breakdown && Array.isArray(expense.tax_breakdown) && expense.tax_breakdown.length > 0) ? (
                    <>
                      {expense.tax_breakdown.map((t, idx) => (
                        <div key={idx} className={`flex justify-between items-center ${isMobile ? 'px-3 py-2' : 'px-4 py-2.5'} bg-gray-50 rounded-xl`}>
                          <span className={`${isMobile ? 'text-sm' : 'text-sm'} text-gray-600`}>{t.name}{t.rate ? ` (${(t.rate * 100).toFixed(1)}%)` : ''}</span>
                          <span className={`${isMobile ? 'text-sm' : 'text-sm'} font-medium text-gray-900`}>{formatCurrency(t.amount)}</span>
                        </div>
                      ))}
                      <div className={`flex justify-between items-center ${isMobile ? 'px-3 py-2' : 'px-4 py-2.5'} bg-gray-100 rounded-xl`}>
                        <span className={`${isMobile ? 'text-sm' : 'text-sm'} font-medium text-gray-700`}>Total Tax</span>
                        <span className={`${isMobile ? 'text-sm' : 'text-sm'} font-semibold text-gray-900`}>{formatCurrency(expense.tax_breakdown.reduce((sum, t) => sum + t.amount, 0))}</span>
                      </div>
                      <div className={`flex justify-between items-center ${isMobile ? 'px-3 py-2' : 'px-4 py-2.5'} bg-blue-50 rounded-xl`}>
                        <span className={`${isMobile ? 'text-sm' : 'text-sm'} font-medium text-blue-700`}>Grand Total</span>
                        <span className={`${isMobile ? 'text-sm' : 'text-sm'} font-bold text-blue-900`}>{formatCurrency(expense.amount + expense.tax_breakdown.reduce((sum, t) => sum + t.amount, 0))}</span>
                      </div>
                    </>
                  ) : expense.tax_amount ? (
                    <div className={`${isMobile ? 'px-3 py-3 text-base' : 'px-4 py-3.5'} bg-gray-50 rounded-xl text-gray-900`}>
                      {formatCurrency(expense.tax_amount)}
                    </div>
                  ) : (
                    <div className={`${isMobile ? 'px-3 py-3 text-base' : 'px-4 py-3.5'} bg-gray-50 rounded-xl text-gray-400`}>
                      No tax
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Tax Deductible */}
            <div className="flex items-center">
              <label className="flex items-center gap-2 cursor-pointer touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                <input
                  type="checkbox"
                  checked={isTaxDeductible}
                  onChange={(e) => setIsTaxDeductible(e.target.checked)}
                  disabled={!isEditing}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50 h-4 w-4"
                />
                <span className={`${isMobile ? 'text-sm' : 'text-sm'} text-gray-700`}>Tax Deductible</span>
              </label>
            </div>

            {/* Notes */}
            <div>
              <label className={`block ${isMobile ? 'text-xs' : 'text-sm'} font-medium text-gray-700 ${isMobile ? 'mb-1.5' : 'mb-2'}`}>
                Notes
              </label>
              {isEditing ? (
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className={`w-full ${isMobile ? 'px-3 py-3 text-base' : 'px-4 py-3.5 text-base'} bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder:text-gray-400 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all resize-none`}
                  placeholder="Additional notes..."
                />
              ) : (
                <div className={`${isMobile ? 'px-3 py-3 text-base' : 'px-4 py-3.5'} bg-gray-50 rounded-xl text-gray-900 min-h-[60px]`}>
                  {expense.notes || '—'}
                </div>
              )}
            </div>

            {/* Linked Booking/Project */}
            {(hasCalendarApp || hasProjectsApp) && (
              <div className="border-t border-gray-200 pt-5">
                <label className={`block ${isMobile ? 'text-xs' : 'text-sm'} font-medium text-gray-700 mb-3`}>
                  Assignment
                </label>
                
                <div className="space-y-3">
                  {hasCalendarApp && (
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                      <div className="flex-1 min-w-0">
                        <div className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-gray-900`}>Booking</div>
                        {isLoadingLinks ? (
                          <div className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-500 mt-1`}>Loading...</div>
                        ) : linkedBooking ? (
                          <div className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-600 mt-1 truncate`}>
                            {linkedBooking.booking_number ? `#${linkedBooking.booking_number} - ` : ''}
                            {linkedBooking.title}
                          </div>
                        ) : (
                          <div className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-400 mt-1`}>Not assigned</div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 ml-3 flex-shrink-0">
                        {linkedBooking && (
                          <button
                            onClick={handleUnlinkBooking}
                            className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors touch-manipulation"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                            title="Unlink booking"
                          >
                            <Unlink className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => setIsSelectBookingOrProjectModalOpen(true)}
                          className={`${isMobile ? 'px-2.5 py-1.5 text-xs' : 'px-3 py-1.5 text-sm'} font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors flex items-center gap-1.5 touch-manipulation`}
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <Link2 className="h-3.5 w-3.5" />
                          {linkedBooking ? 'Change' : 'Assign'}
                        </button>
                      </div>
                    </div>
                  )}

                  {hasProjectsApp && (
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                      <div className="flex-1 min-w-0">
                        <div className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-gray-900`}>Project</div>
                        {isLoadingLinks ? (
                          <div className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-500 mt-1`}>Loading...</div>
                        ) : linkedProject ? (
                          <div className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-600 mt-1 truncate`}>
                            {linkedProject.title}
                          </div>
                        ) : (
                          <div className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-400 mt-1`}>Not assigned</div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 ml-3 flex-shrink-0">
                        {linkedProject && (
                          <button
                            onClick={handleUnlinkProject}
                            className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors touch-manipulation"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                            title="Unlink project"
                          >
                            <Unlink className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => setIsSelectBookingOrProjectModalOpen(true)}
                          className={`${isMobile ? 'px-2.5 py-1.5 text-xs' : 'px-3 py-1.5 text-sm'} font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors flex items-center gap-1.5 touch-manipulation`}
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <Link2 className="h-3.5 w-3.5" />
                          {linkedProject ? 'Change' : 'Assign'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* AI Info */}
            {expense.ai_categorized && expense.ai_extracted_data && (
              <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="h-5 w-5 text-blue-600" />
                  <span className="font-semibold text-blue-900">AI Extraction Details</span>
                </div>
                <div className={`${isMobile ? 'text-xs' : 'text-sm'} text-blue-700 space-y-1`}>
                  <div>Suggested Category: {expense.ai_category_suggestion}</div>
                  {expense.ai_confidence_score && (
                    <div>Confidence: {(expense.ai_confidence_score * 100).toFixed(0)}%</div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          {isEditing && (
            <div className={`sticky bottom-0 bg-white border-t border-gray-200 ${isMobile ? 'px-4 py-3' : 'px-6 py-4'}`}>
              {isMobile ? (
                <div className="flex flex-col gap-2">
                  <button
                    onClick={handleSave}
                    disabled={isSaving || !amount || !description || !expenseDate}
                    className="w-full px-4 py-3.5 text-base bg-blue-600 text-white rounded-xl hover:bg-blue-700 active:bg-blue-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </button>
                  <button
                    onClick={() => { setIsEditing(false); resetForm(); }}
                    className="w-full px-4 py-3 text-base text-gray-700 hover:bg-gray-100 active:bg-gray-200 rounded-xl transition-colors touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-end gap-3">
                  <button
                    onClick={() => { setIsEditing(false); resetForm(); }}
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving || !amount || !description || !expenseDate}
                    className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {createPortal(modalContent, document.body)}
      
      {(hasCalendarApp || hasProjectsApp) && expense && (
        <SelectBookingOrProjectModal
          isOpen={isSelectBookingOrProjectModalOpen}
          onClose={() => setIsSelectBookingOrProjectModalOpen(false)}
          onSelectBooking={handleSelectBooking}
          onSelectProject={handleSelectProject}
          businessId={expense.business_id}
          hasCalendarApp={hasCalendarApp}
          hasProjectsApp={hasProjectsApp}
        />
      )}
    </>
  );
}
